import { useState, useEffect } from "react";
import moment from "moment"; 
import {
    COMMAND,
    GENERIC_REQUEST,
    getApiPath,
    jsonToQueryString,
    PARTICULAR_REQUEST_PARAMETER_LIST_GET,
    PARTICULAR_RESPONSE_PARAMETER_LIST_GET,
    VIEW,
} from "../api/ApiConst";
import ApiCaller from "../api/ApiCaller";

const useRequestDetailedData = (requestId, itemInfo = null, needResponseData = false) => {
    const [request, setRequest] = useState(null);
    const [requestParameters, setRequestParameters] = useState([]);
    const [requestResponseParameters, setRequestResponseParameters] = useState([]);
    const [requestResponseData, setRequestResponseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchRequestDetail = (id) => {
        return new Promise((resolve, reject) => {
            ApiCaller.callGetApi(
                getApiPath(GENERIC_REQUEST, VIEW, id),
                {},
                (response) => {
                    setRequest(response);
                    resolve(response);
                },
                (err) => {
                    console.error("Error fetching request detail:", err);
                    reject(err);
                }
            );
        });
    };

    const fetchRequestParameterList = (id) => {
        
        return new Promise((resolve, reject) => {
            ApiCaller.callPostApi(
                PARTICULAR_REQUEST_PARAMETER_LIST_GET.replace("{request-id}", id),
                null,
                {},
                (response) => {
                    setRequestParameters(response);
                    resolve(response);
                },
                (err) => {
                    console.error("Error fetching request parameter list:", err);
                    reject(err);
                }
            );
        });
    };
    const fetchResponseParameterList = (id) => {
        return new Promise((resolve, reject) => {
            ApiCaller.callPostApi(
                PARTICULAR_RESPONSE_PARAMETER_LIST_GET.replace("{request-id}", id),
                null,
                {},
                (response) => {
                    setRequestResponseParameters(response.data);
                    resolve(response);
                },
                (err) => {
                    console.error("Error fetching response parameter list:", err);
                    reject(err);
                }
            );
        });
    };

    useEffect(() => {
      if (request && needResponseData) {
              let requestData = createRequestPacket(itemInfo).defaultParameters;
              fetchRequestResponseData(request,requestData=== null?[]:requestData);
      }
    }, [request, needResponseData])
    

    const fetchRequestResponseData = (request, formData) => {
        return new Promise((resolve, reject) => {
            const requestUrl = getApiPath(request.name, COMMAND);

            if (request.type === "GET") {
                const finalUrl =
                    Object.keys(formData).length > 0
                        ? `${requestUrl}?${jsonToQueryString(formData)}`
                        : requestUrl;

                ApiCaller.callGetApi(
                    finalUrl,
                    {},
                    (response) => {
                        if(request.responseType === "OBJECT_VIEW"){
                            setRequestResponseData(response.responsePacket[0]);
                        }else{
                            setRequestResponseData(response.responsePacket);
                        }
                        
                        resolve(response);
                    },
                    (err) => {
                        console.error("Error on GET request:", err);
                        reject(err);
                    }
                );
            } else {
                ApiCaller.callPostApi(
                    requestUrl,
                    formData,
                    {},
                    (response) => {
                        if(request.responseType === "OBJECT_VIEW"){
                            setRequestResponseData(response.responsePacket[0]);
                        }else{
                            setRequestResponseData(response.responsePacket);
                        }
                        resolve(response);
                    },
                    (err) => {
                        console.error("Error on POST request:", err);
                        reject(err);
                    }
                );
            }
        });
    };

    const fetchData = () => {
        setLoading(true);
        try {
            // Sequentially fetch data
            fetchRequestDetail(requestId);
            fetchRequestParameterList(requestId);
            fetchResponseParameterList(requestId);
            setLoading(false);
        } catch (err) {
            setError(err.message || "Error occurred while fetching data");
            setLoading(false);
        }
    };

    const createRequestPacket = (packet) => {
        // Clone the input object to avoid mutations
        // If range is true, add range-specific parameters
        if (packet.range !== undefined && packet.range) {
            if (packet.rangeType === "date") {
                const today = moment().format("YYYY-MM-DD");
                const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    
                // Map defaultRangeValue to actual date values
                let startDate, endDate;
                switch (packet.defaultRangeValue) {
                    case "TODAY":
                        startDate = today;
                        endDate = today;
                        break;
                    case "YESTERDAY":
                        startDate = yesterday;
                        endDate = yesterday;
                        break;
                    case "THIS_WEEK":
                        startDate = moment().startOf("week").format("YYYY-MM-DD");
                        endDate = moment().endOf("week").format("YYYY-MM-DD");
                        break;
                    case "LAST_WEEK":
                        startDate = moment().subtract(1, "week").startOf("week").format("YYYY-MM-DD");
                        endDate = moment().subtract(1, "week").endOf("week").format("YYYY-MM-DD");
                        break;
                    case "THIS_MONTH":
                        startDate = moment().startOf("month").format("YYYY-MM-DD");
                        endDate = moment().endOf("month").format("YYYY-MM-DD");
                        break;
                    case "LAST_MONTH":
                        startDate = moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD");
                        endDate = moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD");
                        break;
                    case "THIS_YEAR":
                        startDate = moment().startOf("year").format("YYYY-MM-DD");
                        endDate = moment().endOf("year").format("YYYY-MM-DD");
                        break;
                    case "LAST_YEAR":
                        startDate = moment().subtract(1, "year").startOf("year").format("YYYY-MM-DD");
                        endDate = moment().subtract(1, "year").endOf("year").format("YYYY-MM-DD");
                        break;
                    default:
                        console.error("Invalid defaultRangeValue");
                        startDate = null;
                        endDate = null;
                }
                
                // Add start and end date parameters if valid
                if (startDate && endDate) {
                    packet.defaultParameters = {
                        ...packet.defaultParameters,
                        [packet.startRange]: startDate,
                        [packet.endRange]: endDate,
                    };
                }
            } else if (packet.rangeType === "number") {
                // Handle numeric range (optional, if applicable)
                const rangeParts = packet.defaultRangeValue.split("-");
                const startNumber = rangeParts[0];
                const endNumber = rangeParts[1];
    
                packet.defaultParameters = {
                    ...packet.defaultParameters,
                    [packet.startRange]: startNumber,
                    [packet.endRange]: endNumber,
                };
            }
        }
        
        return packet;
    };

    useEffect(() => {
        fetchData();
    }, [requestId, itemInfo]);

    return {
        request,
        requestParameters,
        requestResponseParameters,
        requestResponseData,
        loading,
        error,
    };
};

export default useRequestDetailedData;
